import { type ActiveRoomSnapshot, ROOM_PROTOCOL_VERSION, type RoomMessage } from "@topzle/types";
import { runtimeId } from "./runtime-id";

const SOCKET_PING = "topzle:ping";
const SOCKET_PONG = "topzle:pong";

export type {
  CloudflareRoomCallOptions,
  RealtimeSessionDescription,
  RealtimeTrackRequest,
  RealtimeTracksResponse,
} from "./media";
export { CloudflareRoomCall } from "./media";
export {
  getPeerGroupMediaProfile,
  type PeerGroupMediaProfile,
  type PeerMediaSignal,
  PeerToPeerRoomCall,
  type PeerToPeerRoomCallOptions,
} from "./p2p-media";
export type RoomConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "offline"
  | "closed";
export interface RoomConnectionState {
  status: RoomConnectionStatus;
  snapshot: ActiveRoomSnapshot | null;
  lastSequence: number;
  error: string | null;
}
export interface RoomConnectionOptions {
  url: string;
  roomId: string;
  participantId: string;
  reconnectToken: string;
  onState: (state: RoomConnectionState) => void;
  onMessage?: (message: RoomMessage) => void;
  socketFactory?: (url: string) => WebSocket;
}
export class RoomConnection {
  private socket: WebSocket | null = null;
  private retry = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lastPongAt = 0;
  private generation = 0;
  private disposed = false;
  private state: RoomConnectionState = {
    status: "idle",
    snapshot: null,
    lastSequence: 0,
    error: null,
  };
  constructor(private readonly options: RoomConnectionOptions) {}
  connect() {
    if (this.disposed) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.clearHeartbeat();
    const previousSocket = this.socket;
    if (previousSocket) {
      this.detachSocket(previousSocket);
      if (
        previousSocket.readyState === WebSocket.CONNECTING ||
        previousSocket.readyState === WebSocket.OPEN
      )
        previousSocket.close(1000, "connection replaced");
    }
    const generation = ++this.generation;
    this.update({ status: this.retry ? "reconnecting" : "connecting", error: null });
    const url = new URL(this.options.url);
    url.searchParams.set("participantId", this.options.participantId);
    url.searchParams.set("reconnectToken", this.options.reconnectToken);
    url.searchParams.set("lastSequence", String(this.state.lastSequence));
    let socket: WebSocket;
    try {
      socket = (this.options.socketFactory ?? ((value) => new WebSocket(value)))(url.toString());
    } catch {
      this.update({ error: "The room connection was interrupted" });
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      if (this.disposed || generation !== this.generation || this.socket !== socket) return;
      this.retry = 0;
      this.lastPongAt = Date.now();
      this.update({ status: "connected", error: null });
      this.clearHeartbeat();
      try {
        socket.send(SOCKET_PING);
      } catch {
        socket.close(4000, "Room heartbeat could not be sent");
        return;
      }
      this.heartbeat = setInterval(() => {
        if (this.socket !== socket || socket.readyState !== WebSocket.OPEN) return;
        if (Date.now() - this.lastPongAt > 45_000) {
          socket.close(4000, "Room heartbeat timed out");
          return;
        }
        try {
          socket.send(SOCKET_PING);
        } catch {
          socket.close(4000, "Room heartbeat could not be sent");
        }
      }, 20_000);
    };
    socket.onmessage = (event) => {
      if (generation !== this.generation || this.socket !== socket) return;
      this.lastPongAt = Date.now();
      if (this.state.error === "The room connection was interrupted") this.update({ error: null });
      this.receive(String(event.data));
    };
    socket.onclose = (event) => {
      if (generation !== this.generation || this.socket !== socket) return;
      this.clearHeartbeat();
      this.socket = null;
      if (event.code === 4002) {
        this.disposed = true;
        this.update({
          status: "closed",
          error: "This room was opened in another tab or device.",
        });
        return;
      }
      if (this.disposed || event.code === 1000) this.update({ status: "closed", error: null });
      else this.scheduleReconnect();
    };
    socket.onerror = () => {
      if (generation === this.generation && this.socket === socket)
        this.update({ error: "The room connection was interrupted" });
    };
  }
  send(type: RoomMessage["type"], payload: unknown) {
    return this.sendTracked(type, payload) !== null;
  }
  sendTracked(type: RoomMessage["type"], payload: unknown): string | null {
    if (this.socket?.readyState !== WebSocket.OPEN) return null;
    const id = runtimeId();
    const message: RoomMessage = {
      version: ROOM_PROTOCOL_VERSION,
      id,
      type,
      roomId: this.options.roomId,
      senderId: this.options.participantId,
      clientTimestamp: Date.now(),
      payload,
    };
    try {
      this.socket.send(JSON.stringify(message));
    } catch {
      this.update({ error: "The room connection was interrupted" });
      this.socket.close(4000, "Room message could not be sent");
      return null;
    }
    return id;
  }
  close() {
    this.disposed = true;
    this.generation += 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.clearHeartbeat();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      this.detachSocket(socket);
      if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)
        socket.close(1000, "client closed");
    }
    this.update({ status: "closed", error: null });
  }
  private receive(raw: string) {
    if (raw === SOCKET_PONG) return;
    try {
      const message = JSON.parse(raw) as RoomMessage;
      if (message.version !== ROOM_PROTOCOL_VERSION) throw new Error("Protocol version mismatch");
      if (
        message.sequence &&
        message.sequence <= this.state.lastSequence &&
        message.type !== "room.snapshot" &&
        message.type !== "reconnect.accepted"
      )
        return;
      if (message.sequence) this.state.lastSequence = message.sequence;
      if (message.type === "room.snapshot")
        this.state.snapshot = message.payload as ActiveRoomSnapshot;
      else if (this.state.snapshot) {
        const snapshot = this.state.snapshot;
        if (
          message.type === "game.state" ||
          message.type === "game.started" ||
          message.type === "game.switched" ||
          message.type === "game.resumed"
        ) {
          const payload = message.payload as {
            gameId?: string;
            state?: ActiveRoomSnapshot["gameState"];
            roomResults?: ActiveRoomSnapshot["roomResults"];
          };
          this.state.snapshot = {
            ...snapshot,
            currentGameId: payload.gameId ?? snapshot.currentGameId,
            gameState: payload.state ?? snapshot.gameState,
            roomResults: payload.roomResults ?? snapshot.roomResults,
            sequence: message.sequence ?? snapshot.sequence,
            updatedAt: message.serverTimestamp ?? Date.now(),
          };
        } else if (message.type === "game.ended") {
          this.state.snapshot = {
            ...snapshot,
            currentGameId: null,
            gameState: null,
            sequence: message.sequence ?? snapshot.sequence,
            updatedAt: message.serverTimestamp ?? Date.now(),
          };
        } else if (message.type === "media.state") {
          const payload = message.payload as {
            participantId?: string;
            microphone?: boolean;
            camera?: boolean;
            connected?: boolean;
            connectionQuality?: ActiveRoomSnapshot["participants"][number]["connectionQuality"];
            mediaSessionId?: string;
            audioTrackId?: string;
            videoTrackId?: string;
          };
          this.state.snapshot = {
            ...snapshot,
            participants: snapshot.participants.map((participant) => {
              if (participant.id !== payload.participantId) return participant;
              const updated = {
                ...participant,
                ...(typeof payload.microphone === "boolean"
                  ? { microphone: payload.microphone }
                  : {}),
                ...(typeof payload.camera === "boolean" ? { camera: payload.camera } : {}),
                ...(typeof payload.connected === "boolean" ? { connected: payload.connected } : {}),
                ...(payload.connectionQuality
                  ? { connectionQuality: payload.connectionQuality }
                  : {}),
              };
              if (payload.mediaSessionId) updated.mediaSessionId = payload.mediaSessionId;
              else delete updated.mediaSessionId;
              if (payload.audioTrackId) updated.audioTrackId = payload.audioTrackId;
              else delete updated.audioTrackId;
              if (payload.videoTrackId) updated.videoTrackId = payload.videoTrackId;
              else delete updated.videoTrackId;
              return updated;
            }),
            sequence: message.sequence ?? snapshot.sequence,
            updatedAt: message.serverTimestamp ?? Date.now(),
          };
        } else if (message.type === "room.presence") {
          const payload = message.payload as {
            participantId?: string;
            online?: boolean;
            activeInRoom?: boolean;
          };
          this.state.snapshot = {
            ...snapshot,
            participants: snapshot.participants.map((participant) =>
              participant.id === payload.participantId
                ? {
                    ...participant,
                    ...(typeof payload.online === "boolean" ? { connected: payload.online } : {}),
                    ...(typeof payload.activeInRoom === "boolean"
                      ? { activeInRoom: payload.activeInRoom }
                      : {}),
                  }
                : participant,
            ),
            sequence: message.sequence ?? snapshot.sequence,
            updatedAt: message.serverTimestamp ?? Date.now(),
          };
        } else if (message.type === "room.join") {
          const joined = message.payload as ActiveRoomSnapshot["participants"][number];
          this.state.snapshot = {
            ...snapshot,
            participants: [
              ...snapshot.participants.filter((participant) => participant.id !== joined.id),
              joined,
            ],
            sequence: message.sequence ?? snapshot.sequence,
            updatedAt: message.serverTimestamp ?? Date.now(),
          };
        } else if (message.type === "room.leave" && message.senderId) {
          this.state.snapshot = {
            ...snapshot,
            participants: snapshot.participants.map((participant) =>
              participant.id === message.senderId
                ? {
                    ...participant,
                    connected: false,
                    speaking: false,
                    connectionQuality: "offline",
                  }
                : participant,
            ),
            sequence: message.sequence ?? snapshot.sequence,
            updatedAt: message.serverTimestamp ?? Date.now(),
          };
        }
      }
      this.options.onState({ ...this.state });
      // Publish transport state first. Consumers often derive richer room state
      // from the message; calling onState afterwards used to overwrite that
      // derived state with the connection's older snapshot.
      this.options.onMessage?.(message);
    } catch (error) {
      this.update({ error: error instanceof Error ? error.message : "Invalid room update" });
    }
  }
  private scheduleReconnect() {
    if (this.disposed || this.reconnectTimer) return;
    const online = typeof navigator === "undefined" || navigator.onLine;
    this.update({ status: online ? "reconnecting" : "offline" });
    const delay = Math.min(15_000, 500 * 2 ** this.retry) + this.retry * 73;
    this.retry += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
  private clearHeartbeat() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }
  private detachSocket(socket: WebSocket) {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
  }
  private update(patch: Partial<RoomConnectionState>) {
    this.state = { ...this.state, ...patch };
    this.options.onState({ ...this.state });
  }
}
